import { useState, useRef, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import cls from "classnames";
import { useQuery } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useNavigate } from "react-router-dom";

import { userInfoAtom, vipModalShowAtom } from "@/atom/user";
import {
  isWorkLeftPanelOpenAtom,
  selectedBgAtom,
  workDataAtom,
} from "@/atom/drawBoard";

import { UserInfo } from "@/services/User/userTypes";
import { fetchUserInfo } from "@/services/User/userApi";
import ModalVip from "@/components/ModalVip";
import Modal from "@/components/Modal";
import styles from "./index.module.scss";
import ControlPanel from "@/components/ControlPanel";
import LeftHeader from "@/components/LeftHeader";
import { getCoursePageSettings } from "@/services/Course/courseApi";

const NO_NEED_LOGIN_PAGE = [
  "/enter",
  "/register",
  "/introduction",
  "/passwordfind",
  "/allcourses",
  "/courseinfo",
  "/allproblems",
  "/",
];

const SIDEBAR_WIDTH = 260;
const LEFT_HEADER_WIDTH = 120;

const Layout = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useAtom(userInfoAtom);
  const vipModalShow = useAtomValue(vipModalShowAtom);
  const [showShareModal, setShareShowModal] = useState(false);
  const [shareQrcode, setShareQrcode] = useState("");
  const [workData] = useAtom(workDataAtom);
  const location = useLocation();
  const [isWorkLeftPanelOpen, setIsWorkLeftPanelOpen] = useAtom(
    isWorkLeftPanelOpenAtom
  );

  const [contentWidth, setContentWidth] = useState(window.innerWidth);

  const userId = useRef("");
  const selectedBg = useAtomValue(selectedBgAtom);
  const [gotUserInfo, setGotUserInfo] = useState(false);
  const [mainLeftWidth, setMainLeftWidth] = useState(0);

  const isCourse = location.pathname.toLowerCase() === "/course";

  const isWork = location.pathname.indexOf("work") > -1;
  const isPersonalPage =
    location.pathname.toLowerCase() === "/download" ||
    location.pathname.toLowerCase() === "/personalinfo" || 
    location.pathname.toLowerCase() === "/passwordreset" ||
    location.pathname.toLowerCase() === "/infoedit"  ||
    location.pathname.toLowerCase() === "/passwordfind" ||
    location.pathname.toLowerCase() === "/enter"  || 
    location.pathname.toLowerCase() === "/register"

  // const showFooter = !isMobile && gotUserInfo && !userInfo?.userId;


  useEffect(() => {
    setMainLeftWidth(
      isCourse || isWork || !userInfo?.userId ? 0 : LEFT_HEADER_WIDTH
    );
  }, [userInfo, isCourse, isWork]);

  useEffect(() => {
    const handleResize = () => {
      setContentWidth(
        window.innerWidth -
          (isWorkLeftPanelOpen ? SIDEBAR_WIDTH : mainLeftWidth)
      );
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isWorkLeftPanelOpen, mainLeftWidth]);

  useEffect(() => {
    async function getData() {
      let res = await getCoursePageSettings("console_setting");

      if (res.code === 0 && res.msg === "success" && !isCourse) {
        setIsWorkLeftPanelOpen(
          !res.data?.workbench || res.data?.workbench === "收起" ? false : true
        );
      }
    }

    getData();
  }, []);

  useEffect(() => {
    if (!userInfo) setIsWorkLeftPanelOpen(false);
  }, [userInfo]);

  const { data } = useQuery<UserInfo>({
    queryKey: ["fetchUserInfo"],
    queryFn: async () => {
      try {
        const data = await fetchUserInfo();
        setGotUserInfo(true);
        if (data.code !== 0) {
          if (NO_NEED_LOGIN_PAGE.includes(location.pathname)) {
            console.log(
              "fetchUserInfo failed, but these page no need to redirect to /enter"
            );
          } else {
            navigate("/enter");
          }
        }
        setUserInfo(data.data);
        userId.current = data.data?.userId;
        return data.data;
      } catch (error) {
        throw new Error("fetchUserInfo failed");
      }
    },
  });

  return (
    <div
      className={cls({
        [styles.container]: true,
        [styles.personalPage]: isPersonalPage,
      })}
    >
      <LeftHeader
        selectedBgType={selectedBg?.type}
        title={workData?.lessonTitle || workData?.workName}
        courseId={workData?.courseId}
        userInfo={userInfo}
        gotUserInfo={gotUserInfo}
        isLessonEnd={workData?.lessonEnd}
      ></LeftHeader>

      <div
        style={{
          width: isWorkLeftPanelOpen ? SIDEBAR_WIDTH : 0,
          minWidth: 0,
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          overflow: "hidden",
          transition: "width 0.2s",
          zIndex: 10,
        }}
      >
        <ControlPanel></ControlPanel>
      </div>

      <div
        className={styles.main}
        style={{
          marginLeft: isWorkLeftPanelOpen ? SIDEBAR_WIDTH : mainLeftWidth,
          width: isWork ? "100%" : contentWidth,
          transition: "margin-left 0.2s",
        }}
      >
        <Outlet />
        {/* {showFooter && <Footer />} */}
      </div>

      {vipModalShow && <ModalVip />}
      <Modal
        title=""
        description={
          <>
            请用手机微信扫描二维码查看
            <div className={styles.boxQrcode}>
              <img src={shareQrcode} alt="分享二维码" />
            </div>
          </>
        }
        showModal={showShareModal}
        setShowModal={setShareShowModal}
        onConfirm={() => {}}
        confirmTxt="确定"
        cancelTxt=""
        hasClose={true}
        onClose={() => setShareShowModal(false)}
      />
    </div>
  );
};

export default Layout;
